import { useContext, useEffect, useRef, useState } from "react";
import { AppLayout } from "../../../../containers/layout/app";
import { EditLoanApplicationWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../../assets";
import { useNavigate, useParams } from "react-router-dom";
import {
	Box,
	Chip,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { BaseButton } from "../../../../components/button/styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllCustomersService } from "../../../../utils/api/customerservice/customerlist/retrieveAllCustomers";
import { retrieveAllLoanProductService } from "../../../../utils/api/configuration/loanproduct/retrieveAllLoanProduct";
import { updateLoanApplicationService } from "../../../../utils/api/loanmanagement/loansetup/updateLoanApplication";
import { retrieveLoanService } from "../../../../utils/api/loanmanagement/loansetup/retrieveLoan";
import { retrieveLoanProductService } from "../../../../utils/api/configuration/loanproduct/retrieveLoanProduct";
import { retrieveSavingsAccountByCustomerIdService } from "../../../../utils/outbound/savingsaccount/retrieveSavingsAccountByCustomerId";
import { formatAmountDisplay } from "../../../../helpers/formatAmountDisplay";

export const EditLoanApplication = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { id = "" } = useParams();
	const { setActiveNavItem } = useContext(AppContext);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [institutionId, setInstitutionId] = useState<string | null>(null);
	const [selectedLoanProduct, setSelectedLoanProduct] = useState<Record<
		string,
		any
	> | null>(null);
	const [formDetails, setFormDetails] = useState<{
		obligor: string;
		disbursementAccount: string;
		loanProduct: string;
		loanId: string;
		term: string;
		interestRate: string;
		disbursementDate: string;
		firstRepaymentDate: string;
		appliedAmount: string;
		// penalty: string;
		attachments: File[];
		description: string;
	}>({
		obligor: " ",
		disbursementAccount: "",
		loanProduct: " ",
		loanId: "",
		term: "",
		interestRate: "",
		disbursementDate: "",
		firstRepaymentDate: "",
		appliedAmount: "",
		// penalty: "",
		attachments: [],
		description: "",
	});

	useEffect(() => {
		setActiveNavItem("Loan Management");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (!id) return;
		retrieveLoanService(TOKEN, id)
			.then(async (data) => {
				setInstitutionId(data.institution);
				const attachments = await convertUrlToFile(data.attachments);
				setFormDetails({
					obligor: data.obligor,
					disbursementAccount: data.disbursementAccount,
					loanProduct: data.loanProductId,
					loanId: data.serialNo,
					term: data.term,
					interestRate: data.interestRate,
					disbursementDate: new Date(data.disbursementDate)
						.toISOString()
						.split("T")[0],
					firstRepaymentDate: new Date(data.firstRepaymentDate)
						.toISOString()
						.split("T")[0],
					appliedAmount: data.appliedAmount,
					// penalty: data.penalty,
					attachments: attachments || [],
					description: data.description || "",
				});
			})
			.catch((err) => {
				console.error("Failed to fetch loan application:", err);
			});
	}, [TOKEN, id]);

	useEffect(() => {
		setSelectedLoanProduct(null);
		const fetchLoanProduct = async (loanProductId: string) => {
			try {
				if (!formDetails.loanProduct.trim()) return;
				const response = await retrieveLoanProductService(TOKEN, loanProductId);
				if (response) {
					setSelectedLoanProduct(response);
				} else {
					setSelectedLoanProduct(null);
				}
			} catch (error) {
				console.error("Failed to fetch loan product:", error);
			}
		};
		fetchLoanProduct(formDetails.loanProduct);
	}, [TOKEN, formDetails.loanProduct]);

	const fetchObligorSavingsAccount = async (obligorId: string) => {
		try {
			if (!obligorId.trim()) return;
			const response = await retrieveSavingsAccountByCustomerIdService(
				TOKEN,
				obligorId,
			);
			return setFormDetails((prev) => ({
				...prev,
				disbursementAccount: response.accountNumber,
			}));
		} catch (error) {
			return console.error("Failed to fetch obligor savings account:", error);
		}
	};

	const { data: customers } = useQuery({
		queryKey: ["customers", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllCustomersService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: loanProducts } = useQuery({
		queryKey: ["loanProducts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllLoanProductService(TOKEN);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const convertUrlToFile = async (attachments: string[]): Promise<File[]> => {
		const files: File[] = [];
		for (const url of attachments) {
			try {
				const response = await fetch(url);
				const blob = await response.blob();
				const name = url.split("/loan_application_attachment/")[1];
				const file = new File([blob], name, { type: blob.type });
				files.push(file);
			} catch (err) {
				console.error("Failed to fetch attachment:", url, err);
			}
		}
		return files;
	};

	const handleChange = async (
		e:
			| SelectChangeEvent<unknown>
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name, type, value } = e.target as HTMLInputElement;
		if (type === "file") {
			const input = e.target as HTMLInputElement;
			const fileArray = input.files ? Array.from(input.files) : [];
			setFormDetails((prev) => ({
				...prev,
				[name]: [...(prev[name as keyof typeof prev] as File[]), ...fileArray],
			}));
		} else {
			setFormDetails((prev) => ({
				...prev,
				[name]: value,
				...(name === "loanProduct" && {
					term: "",
					interestRate: "",
					appliedAmount: "",
				}),
				...(name === "obligor" && { disbursementAccount: "" }),
			}));
			if (name === "obligor") {
				await fetchObligorSavingsAccount(value);
				return;
			}
		}
		return;
	};

	const handleDeleteAttachment = (fileToDelete: File) => {
		setFormDetails((prev) => {
			const updated = prev.attachments.filter((file) => file !== fileToDelete);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			return {
				...prev,
				attachments: updated,
			};
		});
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		if (!institutionId) return;
		const formData = new FormData();
		const data = {
			obligor: formDetails.obligor,
			disbursementAccount: formDetails.disbursementAccount,
			loanProduct: formDetails.loanProduct,
			loanId: formDetails.loanId,
			term: formDetails.term,
			interestRate: formDetails.interestRate,
			disbursementDate: formDetails.disbursementDate,
			firstRepaymentDate: formDetails.firstRepaymentDate,
			appliedAmount: formDetails.appliedAmount,
			// penalty: formDetails.penalty,
			description: formDetails.description,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		formDetails.attachments.forEach((file, index) => {
			if (file.size > 0 && file.type !== "text/html") {
				formData.append("loanApplicationAttachment", file);
			}
		});
		try {
			const response = await updateLoanApplicationService(
				TOKEN,
				institutionId,
				id,
				formData,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to update loan application draft. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to update loan application draft. ${error.message}`);
			console.error("Failed to update loan application draft:", error);
		}
	};

	return (
		<AppLayout pageId={"Book-Loan"} pageTitle={"Edit Loan Application"}>
			<EditLoanApplicationWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title="Loan Application Draft Updated Successfully"
					message="Your information has been successfully updated."
					callToAction="Back to Loan Application List"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter required information to update loan application draft
					</Typography>
				</Box>
				<form onSubmit={handleSubmit}>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Obligor</BaseLabel>
								<BaseSelect
									name="obligor"
									fontweight={400}
									value={formDetails.obligor}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Customer</BaseOption>
									{customers?.map(
										(customer: Record<string, any>, index: number) => (
											<BaseOption key={index} value={customer.id}>
												{`${customer?.firstName} ${customer?.lastName} `
													?.split(" ")
													.map(
														(word: string) =>
															word.charAt(0).toUpperCase() + word.slice(1),
													)
													.join(" ")}
											</BaseOption>
										),
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Disbursement Account Number
								</BaseLabel>
								<BaseInput
									required
									readOnly
									name="disbursementAccount"
									value={formDetails.disbursementAccount}
									placeholder="Obligor Account Number"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Loan Product</BaseLabel>
								<BaseSelect
									name="loanProduct"
									fontweight={400}
									value={formDetails.loanProduct}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Loan Product</BaseOption>
									{loanProducts?.map(
										(product: Record<string, any>, index: number) => (
											<BaseOption key={index} value={product.id}>
												{product.name
													?.split(" ")
													.map(
														(word: string) =>
															word.charAt(0).toUpperCase() + word.slice(1),
													)
													.join(" ")}
											</BaseOption>
										),
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Loan ID</BaseLabel>
								<BaseInput
									required
									readOnly
									name="loanId"
									value={formDetails.loanId.toString().padStart(3, "0")}
									placeholder="Loan ID"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Loan Term</BaseLabel>
								<BaseInput
									required
									name="term"
									value={formDetails.term}
									placeholder="Loan Term"
									onChange={(e) => handleChange(e)}
								/>
								{selectedLoanProduct && (
									<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
										<Typography
											variant="caption"
											fontFamily={"Inter"}
											fontWeight={"600"}
											fontSize={14}
											lineHeight={"normal"}
											color={"var(--text-salmon-pink-color)"}
											whiteSpace={"normal"}
										>
											{`Allowable term is a duration (in number of ${
												selectedLoanProduct?.term === "annually"
													? "years"
													: selectedLoanProduct?.term === "monthly"
														? "months"
														: selectedLoanProduct?.term === "weekly"
															? "weeks"
															: "days"
											}) less than or equal to ${selectedLoanProduct?.tenor} ${
												selectedLoanProduct?.term === "annually"
													? "years"
													: selectedLoanProduct?.term === "monthly"
														? "months"
														: selectedLoanProduct?.term === "weekly"
															? "weeks"
															: "days"
											}.`}
										</Typography>
									</Box>
								)}
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Monthly Interest Rate
								</BaseLabel>
								<BaseInput
									required
									name="interestRate"
									value={formDetails.interestRate}
									placeholder="Enter Interest Rate"
									onChange={(e) => handleChange(e)}
								/>
								{selectedLoanProduct && (
									<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
										<Typography
											variant="caption"
											fontFamily={"Inter"}
											fontWeight={"600"}
											fontSize={14}
											lineHeight={"normal"}
											color={"var(--text-salmon-pink-color)"}
											whiteSpace={"normal"}
										>
											{`Allowable interest rate is a percentage interest (in number) less than or equal to ${selectedLoanProduct?.interestRate}%.`}
										</Typography>
									</Box>
								)}
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Disbursement Date
								</BaseLabel>
								<BaseInput
									required
									type="date"
									name="disbursementDate"
									value={formDetails.disbursementDate}
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									First Repayment Date
								</BaseLabel>
								<BaseInput
									required
									type="date"
									name="firstRepaymentDate"
									value={formDetails.firstRepaymentDate}
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Applied Amount</BaseLabel>
								<BaseInput
									required
									name="appliedAmount"
									placeholder="Enter Applied Amount"
									value={formDetails.appliedAmount}
									onChange={(e) => handleChange(e)}
								/>
								{selectedLoanProduct && (
									<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
										<Typography
											variant="caption"
											fontFamily={"Inter"}
											fontWeight={"600"}
											fontSize={14}
											lineHeight={"normal"}
											color={"var(--text-salmon-pink-color)"}
											whiteSpace={"normal"}
										>
											{`Allowable application amount is a figure (in number) less than or equal to ${formatAmountDisplay(
												selectedLoanProduct?.amount ?? 0,
											)}.`}
										</Typography>
									</Box>
								)}
							</BaseFieldSet>
						</Grid2>
						{/* <Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Penalty</BaseLabel>
								<BaseInput
									required
									name="penalty"
									placeholder="Enter Penalty"
									value={formDetails.penalty}
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2> */}
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Attachments</BaseLabel>
								<BaseInput
									type="file"
									name="attachments"
									inputRef={fileInputRef}
									inputProps={{
										accept:
											".jpeg,.jpg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx,.rtf,.odt,.ods,.odp",
										multiple: true,
									}}
									onChange={handleChange}
								/>
								{formDetails.attachments.length > 0 && (
									<Stack
										direction={"row"}
										flexWrap={"wrap"}
										gap={"calc(var(--flex-gap)/4)"}
										marginBlockStart={"calc(var(--basic-margin)/4)"}
									>
										{formDetails.attachments.map((file: File, index) => (
											<Chip
												key={index}
												label={file.name}
												onDelete={() => handleDeleteAttachment(file)}
											/>
										))}
									</Stack>
								)}
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Description</BaseLabel>
								<BaseInput
									required
									multiline
									minRows={4}
									name="description"
									value={formDetails.description}
									placeholder="Add any additional details or notes."
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
					</Grid2>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"var(--text-salmon-pink-color)"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box overflow={"hidden"}>
						<BaseButton
							type="submit"
							variant="contained"
							bgcolor="var(--primary-color)"
							disableElevation
							sx={{
								width: { mobile: "100%", miniTablet: "auto" },
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Submit
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
			</EditLoanApplicationWrapper>
		</AppLayout>
	);
};
